<template>
  <component :is="container">
    <RecipeLayout v-if="isRecipe">
      <RecipeErrorContainer :error="props.error" />
    </RecipeLayout>
    <DefaultErrorContaner
      v-else
      :is-health="isHealth"
      :error="props.error"
    />
  </component>
</template>

<script setup lang="ts">
  import { isHealthPath, isRecipeOrRecipesPageByPath } from './utils/router';
  import { createNotFoundSeo, createUnavailableSeo } from '~/domains/head/seo/model/defaults';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';
  import { useTechnicalHead } from '~/domains/head/technical/services/useTechnicalHead';

  // Тут мы включаем layout-ы через NuxtLayout/NuxtPage
  // Однако мы не можем вынести сюда любые стили/компоненты
  // Потому что app.vue общий для сайта и для AMP версии
  // Поэтому тут нужно грузить AppContainer асинхронно
  const AppContainer = defineAsyncComponent(() => import('~/containers/AppContainer.vue'));
  const AmpAppContainer = defineAsyncComponent(() => import('~/containers/AppContainer.amp.vue'));

  const DefaultErrorContaner = defineAsyncComponent(
    () => import('~/components/DefaultErrorContaner.vue'),
  );
  const RecipeErrorContainer = defineAsyncComponent(
    () => import('~/containers/RecipeErrorContainer/RecipeErrorContainer.vue'),
  );
  const RecipeLayout = defineAsyncComponent(() => import('./layouts/recipes.vue'));

  const props = defineProps({
    error: {
      required: true,
      type: Object,
    },
  });

  const route = useRoute();
  const { ssrContext, $logger, $isAMP } = useNuxtApp();
  const container = computed(() => ($isAMP ? AmpAppContainer : AppContainer));
  const isHealth = computed(() => isHealthPath(route.path));
  const isRecipe = computed(() => isRecipeOrRecipesPageByPath(route.path));

  const seo = computed(() =>
    props.error?.statusCode === 404
      ? createNotFoundSeo(route.path)
      : createUnavailableSeo(route.path),
  );

  useTechnicalHead();
  useSeoHead(seo);

  if (ssrContext) {
    ssrContext.event.node.res.statusCode = props.error?.statusCode === 404 ? 404 : 503;
  }

  if (props.error?.statusCode !== 404) {
    $logger?.error(props.error?.statusCode, '🛑️ error page', props.error);
  }
</script>

<style land="scss">
  :root {
    --main-color: #ed5e42;
    --main-color-2: #2962f9;
    --preview-header-color: #a0270f;
    --hover-color: #d44b3f;
    --hover-color-2: #264eb1;
    --bg-color-substrate: #fbf8f8;
    --bg-color-substrate-2: #f3f5fc;
    --bg-color-substrate-3: #f3f5fc;
    --disabled-color: #d4d7de;
  }
</style>
